import {useSearchParams} from "react-router";
import {RatingProfileId, SearchState} from "../models/models";
import {
    MapInfo,
    SearchQueryTag,
    SearchQueryTagType
} from "../models/search_query";
import {useCallback, useMemo} from "react";
import {latLng, latLngBounds} from "leaflet";
import {geoLocationFromSearchQueryTagValue} from "./helpers";



const initialMapBounds = latLngBounds([ 45.675589370927966, 4.944584572461532 ], [ 48.0965285236182, 11.437504494336535 ]);

export function useSearchState(): [ SearchState, (newSearchState: SearchState) => void ] {
    const [searchParams, setSearchParams] = useSearchParams();

    const tagsQuery = searchParams.get("tags") ?? "";
    const placeQueryTag = geoLocationFromSearchQueryTagValue(searchParams.get("center"));
    const { lat, lng } = placeQueryTag ? placeQueryTag : { lat: undefined, lng: undefined }
    const zoom = searchParams.has("zoom") ? parseInt(searchParams.get("zoom") || "16") : 16;
    const ratingProfileId: RatingProfileId = searchParams.has("rating_profile_id") ? parseInt(searchParams.get("rating_profile_id") || "") : null;

    const searchState: SearchState = useMemo(() => {
        const tagValues = tagsQuery.split(";")
        const searchQueryTags = tagValues.filter(Boolean).map(val => {
            const [ type, value ] = val.split(":")
            const tag: SearchQueryTag = { type: type as SearchQueryTagType, value: decodeURIComponent(value) };
            return tag;
        });

        let mapInfo: MapInfo = { bounds: initialMapBounds };
        if (lat && lng) {
            mapInfo = { center: latLng(lat, lng), zoom: zoom };
        }

        const searchState: SearchState = {
            searchQueryTags: searchQueryTags,
            mapInfo: mapInfo,
            ratingProfileId: ratingProfileId
        }
        return searchState;
    }, [tagsQuery, lat, lng, zoom, ratingProfileId]);

    const setSearchState = useCallback((newSearchState: SearchState) => {
        setSearchParams((params) => {
            const { searchQueryTags, mapInfo, ratingProfileId } = newSearchState;

            // serialize SearchQueryTagList and set params
            const values = searchQueryTags.map((tag) => {
                return `${tag.type}:${encodeURIComponent(tag.value)}`;
            }).join(";");

            if (values.length > 0) {
                params.set("tags", values);
            } else {
                params.delete("tags");
            }

            // serialize MapInfo and set params
            if (mapInfo?.center) {
                params.set("center", `${mapInfo.center.lat}/${mapInfo.center.lng}`);
            } else {
                params.delete("center");
            }
            if (mapInfo?.zoom) {
                params.set("zoom", mapInfo.zoom.toString());
            } else {
                params.delete("zoom");
            }

            // serialize ratingProfileId and set params
            if (ratingProfileId === null) {
                params.delete("rating_profile_id");
            } else {
                params.set("rating_profile_id", ratingProfileId.toString());
            }

            return params;
        });
    }, [setSearchParams]);

    return [ searchState, setSearchState ];
}
